// @ts-strict-ignore
import { Subject, Subscription } from 'rxjs';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import xmlParser from 'rainbow-web-sdk/utils/xmlParser';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { Service } from 'rainbow-web-sdk/services/service';
import type { StropheHandler } from 'rainbow-web-sdk';
import { ccdModes } from 'rainbow-web-sdk/models/contact.model';

export const TELEPHONY_CCD_AGENT_SVC = 'TelephonyCCDAgentService';

export class TelephonyCCDAgentService extends Service {

    private CCD_AGENT_NAMESPACE: string = 'urn:xmpp:pbxagent:ccdfeatures:1';

    private xmppManagementHandler: any;
    private rxSubject: Subject<any> = new Subject<any>();
    private ccdAgentHandlerRef: StropheHandler = null;
    public eventNames: string[] = [];
    private groupId: string = "";
    private password: string = "";
    private passwordNeeded: boolean = true;
    private agentId: string = "";
    private passwordPresence: boolean = false;
    private currentGroupId: string = "";
    private logActivated: boolean = false;
    private wrapUpActivated: boolean = false;
    private withdrawalActivated: boolean = false;
    private pauseActivated: boolean = false;
    private state: string = "";
    private reason: string = "";
    private mode: string = "";
    private role: string = "";
    private value: any = {
        "agentId": this.agentId,
        "passwordPresence": this.passwordPresence,
        "currentGroupId": this.currentGroupId,
        "logActivated": this.logActivated,
        "wrapUpActivated": this.wrapUpActivated,
        "withdrawalActivated": this.withdrawalActivated,
        "pauseActivated": this.pauseActivated,
        "reason": this.reason,
        "state": this.state,
        "mode": this.mode,
        "role": this.role
    };
    private eventUnregisters: any[] = [];

    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();

    public static getInstance() {
        return Service.create(TELEPHONY_CCD_AGENT_SVC, TelephonyCCDAgentService);
    }

    public static build() {
        return new TelephonyCCDAgentService();
    }

    protected constructor() { super(); }

    public async start() {
        this.logger.info('[ccdAgentService] === STARTING ===');
        if (!this.isCcdAgent()) {
            this.logger.info(`[ccdAgentService] === Start skipped - ccdAgent : false`);
            return;
        }
        this.logger.info("[ccdAgentService] contact is CCDAgent: true");

        this.connect();
        this.attachHandlers();
        this.eventUnregisters.push(this.eventService.subscribe('ON_TELEPHONY_STATUS_CHANGED_EVENT', (__event, value: string) => {
            if (value === "started") {
                this.getCCDAgentStatus();
            }
        }));
        this.logger.info(`=== STARTED ===`);
    }

    public async connect() {
        try {
            await this.getCCDAgentStatus();
        }
        catch (error) { this.logger.error(`[ccdAgentService] connect -- Failure -- ${error.message}`); }
    }

    public async reconnect() {
        try {
            this.logger.info("[ccdAgentService] reconnect");
            this.attachHandlers();
        }
        catch (error) { this.logger.error(`[ccdAgentService] reconnect -- failure -- ${error.message}`); }
    }


    public async stop() {
        this.logger.info('[ccdAgentService] === STOPPING ===');
        if (this.xmppManagementHandler) {
            this.xmppService.deleteHandler(this.xmppManagementHandler);
            this.xmppManagementHandler = null;
        }
        this.eventUnregisters.forEach((eventUnregister) => { eventUnregister(); });
        this.eventUnregisters = [];
        this.logger.info('=== STOPPED ===');
    }

    public subscribe(observer: any): Subscription {
        return this.rxSubject.subscribe(observer);
    }


    private onCCDAgentStatusMessageReceived = async (stanza: any) => {
        const stanzaElem = xmlParser(stanza);
        if (stanzaElem.find("acdagent").length > 0) {
            const ccdAgentStatusElem = stanzaElem.find("status");
            this.agentId = ccdAgentStatusElem.attr("agentId");
            this.passwordPresence = ccdAgentStatusElem.attr("passwordPresence") === "true";
            this.currentGroupId = ccdAgentStatusElem.attr("currentGroupId");
            this.state = ccdAgentStatusElem.attr("state");
            this.reason = ccdAgentStatusElem.attr("reason");
            this.mode = ccdAgentStatusElem.attr("mode");
            this.role = ccdAgentStatusElem.attr("role");

            this.value.agentId = this.agentId;
            this.value.passwordPresence = this.passwordPresence;
            this.value.currentGroupId = this.currentGroupId;
            this.value.logActivated = this.logActivated = this.state !== "Null";
            this.contactService.userContact.ccdAgent.isCCDAgentLogged = this.logActivated;
            if (this.state !== "Busy") {
                this.value.wrapUpActivated = this.wrapUpActivated = (this.state === "Work After Call" || this.state === "Not Ready Work After Call");
                this.value.withdrawalActivated = this.withdrawalActivated = (this.state === "Not Ready" || this.state === "Not Ready Work After Call" || this.state === "Not Ready Pause");
                this.value.pauseActivated = this.pauseActivated = this.state === "Not Ready Pause";
            }
            this.value.state = this.state;
            this.value.reason = this.reason;
            this.value.mode = this.mode;
            this.value.role = this.role;

            this.logger.info("ccdAgentStatus event received  -- state: " + this.state + " agentId: " + this.agentId + " passwordPresence: " + this.passwordPresence + " currentGroupID:  " + this.currentGroupId + " logActivated: " + this.logActivated + " wrapUpActivated: " + this.wrapUpActivated + " withdrawalActivated: " + this.withdrawalActivated + " pauseActivated: " + this.pauseActivated + " reason: " + this.reason + " mode: " + this.mode + " role: " + this.role);
            this.rxSubject.next(RBEvent.create("ON_CCD_UPDATE_EVENT", this.value));
        }
        if (stanzaElem.find("userccdpolicy").length > 0) {
            this.contactService.userContact.ccdPolicy.onBusyWithdraw = stanzaElem.find("userccdpolicy").attr("onbusyccdwithdraw") === "true";
            this.contactService.userContact.ccdPolicy.onDndWithdraw = stanzaElem.find("userccdpolicy").attr("ondndccdwithdraw") === "true";
            this.contactService.userContact.ccdPolicy.withdrawReason = stanzaElem.find("userccdpolicy").attr("withdrawreason");
            this.contactService.userContact.ccdPolicy.onDndIgnoreVoipCalls = stanzaElem.find("userccdpolicy").attr("ignorevoipcalls") === "true";

            this.logger.info("ccdPolicy event received  -- onBusyWithdraw: " + this.contactService.userContact.ccdPolicy.onBusyWithdraw + " onDndWithdraw: " + this.contactService.userContact.ccdPolicy.onDndWithdraw + " withdrawReason: " + this.contactService.userContact.ccdPolicy.withdrawReason + " onDndIgnoreVoipCalls:  " + this.contactService.userContact.ccdPolicy.onDndIgnoreVoipCalls);
            this.rxSubject.next(RBEvent.create("ON_CCDPOLICY_UPDATE_EVENT", this.value));
        }
        return true;
    };

    private async getCCDAgentStatus(): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/telephony/v1.0/acd/status`;
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) {
                throw (responseData);
            }
            const response: any = await responseData.json();
            const data: any = response.data;

            this.agentId = this.value.agentId = data.agentId;
            this.passwordPresence = this.value.passwordPresence = data.passwordPresence === "true";
            this.currentGroupId = this.value.currentGroupId = data.currentGroupId;
            this.logActivated = this.value.logActivated = data.state !== "Null";
            this.contactService.userContact.ccdAgent.isCCDAgentLogged = this.logActivated;
            if (data.state !== "Busy") {
                this.wrapUpActivated = this.value.wrapUpActivated = (data.state === "Work After Call" || data.state === "Not Ready Work After Call");
                this.withdrawalActivated = this.value.withdrawalActivated = (data.state === "Not Ready" || data.state === "Not Ready Work After Call");
                this.pauseActivated = this.value.pauseActivated = data.state === "Not Ready Pause";
            }
            this.state = this.value.state = data.state;
            this.reason = this.value.reason = data.reason;
            this.mode = this.value.mode = data.mode;
            this.role = this.value.role = data.role;

            this.logger.info(`[CCDAgentService] ccdAgentStatus -- SUCCESS -- State: ` + data.state + " agentId: " + this.agentId + " PasswordPresence: " + this.passwordPresence + " Current Group: " + this.currentGroupId + " logActivated: " + this.logActivated + " wrapUpActivated: " + this.wrapUpActivated + " withdrawalActivated: " + this.withdrawalActivated + " pauseActivated: " + this.pauseActivated + " reason: " + this.reason + " mode: " + this.mode + " role: " + this.role);
            this.rxSubject.next(RBEvent.create("ON_CCD_UPDATE_EVENT", this.value));
            return response.result;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCCDAgentStatus');
            this.logger.error(`[CCDAgentService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getCCDGroupStatus(groupId: string): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/telephony/v1.0/acd/status/group/${encodeURIComponent(groupId)}`;
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) {
                throw (responseData);
            }
            const response: any = await responseData.json();
            const data: any = response.data;

            this.logger.info(`[CCDAgentService] getCCDGroupStatus -- SUCCESS -- groupId: ` + data.groupId + " reasonsize: " + data.reasonsSize);
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCCDGroupStatus');
            this.logger.error(`[CCDAgentService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }


    public async ccdAgentLogOn(endpointTel: string, agentId: string, password: string, groupId: string, nomadicDestination: string, nomadic: boolean, analyticsMode: string = ""): Promise<any> {
        try {
            const analyticsGroupProvided = groupId ? "yes" : "no";
            const analyticsPwdProvided = password ? "yes" : "no";
            const url = `${config.restServerUrl}/api/rainbow/telephony/v1.0/acd/logon?analyticsMode=` + analyticsMode + `&analyticsGroupProvided=` + analyticsGroupProvided + `&analyticsPwdProvided=` + analyticsPwdProvided;
            const headers = this.authService.getPostHeader();
            this.logger.info("[CCDAgentService] ccdAgentLogOn -- endpointTel : " + endpointTel + ", nomadic : " + nomadic + ", agentId : " + agentId + ", groupId : " + groupId);
            const body = { endpointTel: endpointTel, agentId: agentId, password: password, groupId: groupId, nomadicDestination: nomadicDestination, nomadic: nomadic };
            const responseData: any = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
            if (!responseData.ok) {
                throw (responseData);
            }
            const response: any = await responseData.json();
            this.logger.info("[CCDAgentService] ccdAgentLogOn -- SUCCESS");
            this.groupId = groupId;
            this.password = password;
            if (this.password === '') {
                this.passwordNeeded = false;
            }
            return response.result;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'ccdAgentLogOn');
            this.logger.error(`[CCDAgentService] ${errorInfo.message} fullResponseData: ${(error instanceof Response ? JSON.stringify(errorInfo.error.fullResponseData) : "")}`);
            throw errorInfo.error;
        }
    }

    public async ccdAgentLogOff(endpointTel: string, agentId: string, password: string, groupId: string): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/telephony/v1.0/acd/logoff`;
            const headers = this.authService.getPostHeader();
            const body = { endpointTel: endpointTel, agentId: agentId, password: password, groupId: groupId };
            const responseData: any = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
            if (!responseData.ok) {
                throw (responseData);
            }
            const response: any = await responseData.json();
            this.logger.info(`[CCDAgentService] ccdAgentLogOff -- SUCCESS`);
            this.groupId = "";
            this.password = "";
            this.passwordNeeded = true;

            return response.result;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'ccdAgentLogOff');
            this.logger.error(`[CCDAgentService] ${errorInfo.message} fullResponseData: ${(error instanceof Response ? JSON.stringify(errorInfo.error.fullResponseData) : "")}`);
            throw errorInfo.error;
        }
    }

    public async ccdAgentWrapup(agentId: string, groupId: string, password: string, state: string): Promise<any> {
        try {
            const analyticsType = state === "on" ? "yes" : "no";
            const url = `${config.restServerUrl}/api/rainbow/telephony/v1.0/acd/wrapup?analyticsType=` + analyticsType;
            const headers = this.authService.getPostHeader();
            const body = { agentId: agentId, password: password, groupId: groupId, state: state };
            const responseData: any = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
            if (!responseData.ok) {
                throw (responseData);
            }
            const response: any = await responseData.json();
            this.logger.info("[CCDAgentService] ccdAgentLogWrapup " + state + " -- SUCCESS");
            if (this.password === '') {
                this.passwordNeeded = false;
            }

            return response.result;
        }
        catch (error) {
            this.CCDAgentStatus();
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'ccdAgentWrapup');
            this.logger.error(`[CCDAgentService] ${errorInfo.message} fullResponseData: ${(error instanceof Response ? JSON.stringify(errorInfo.error.fullResponseData) : "")}`);
            throw errorInfo.error;
        }
    }

    public async ccdAgentWithdrawal(agentId: string, groupId: string, state: string, reason: string = "1"): Promise<any> {
        try {
            const analyticsType = state === "on" ? "yes" : "no";
            const analyticsReasonProvided = this.reason ? "yes" : "no";
            const url = `${config.restServerUrl}/api/rainbow/telephony/v1.0/acd/withdrawal?analyticsType=` + analyticsType + `&analyticsReasonProvided=` + analyticsReasonProvided;
            const headers = this.authService.getPostHeader();
            const body = { agentId: agentId, groupId: groupId, state: state, reason: reason };
            const responseData: any = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
            if (!responseData.ok) {
                throw (responseData);
            }
            const response: any = await responseData.json();
            this.logger.info("[CCDAgentService] ccdAgentLogWithdrawal " + state + "  reason : " + reason + "  -- SUCCESS");
            if (this.password === '') {
                this.passwordNeeded = false;
            }

            return response.result;
        }
        catch (error) {
            this.CCDAgentStatus();
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'ccdAgentWithdrawal');
            this.logger.error(`[CCDAgentService] ${errorInfo.message} fullResponseData: ${(error instanceof Response ? JSON.stringify(errorInfo.error.fullResponseData) : "")}`);
            throw errorInfo.error;
        }
    }

    private attachHandlers(): void {
        this.logger.info("[ccdAgentService] attachHandlers");
        if (this.ccdAgentHandlerRef) { this.xmppService.deleteHandler(this.ccdAgentHandlerRef); this.ccdAgentHandlerRef = null; }
        // @ts-expect-error strophejs doesn't support promises, but as long as it works don't touch this
        this.ccdAgentHandlerRef = this.xmppService.addHandler((stanza) => { return this.onCCDAgentStatusMessageReceived(stanza); }, null, "message", null);
    }

    public getAgentId(): string {
        return this.agentId;
    }

    public getCCDAgentGroupId(): string {
        return this.groupId;
    }

    public getCCDAgentPasswordPresence(): boolean {
        return this.passwordPresence;
    }

    public isCCDAgentLogActivated(): boolean {
        return this.logActivated;
    }

    public isCCDAgentWrapUpActivated(): boolean {
        return this.wrapUpActivated;
    }

    public isCCDAgentWithdrawalActivated(): boolean {
        return this.withdrawalActivated;
    }

    public isCCDAgentPauseActivated(): boolean {
        return this.pauseActivated;
    }

    public isPasswordNeeded(): boolean {
        return this.passwordNeeded;
    }

    public CCDAgentState(): string {
        return this.state;
    }

    public getCurrentCCDAgentStatus(): any {
        return this.value;
    }

    public CCDAgentStatus(): any {
        this.rxSubject.next(RBEvent.create("ON_CCD_UPDATE_EVENT", this.value));
    }

    public isCcdAgent(): any {
        const isCCDAgentProfilEnabled: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_CCD_AGENT);
        const isCCDAgentFixedMixedProfileEnabled: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_CCD_AGENT_PROFILE);

        return this.contactService.userContact?.ccdAgent &&
            ((isCCDAgentProfilEnabled && this.contactService.userContact.ccdAgent.mode === ccdModes.DEFAULT) ||
            (isCCDAgentFixedMixedProfileEnabled && this.contactService.userContact.ccdAgent.mode !== ccdModes.DEFAULT));
    }

    public isCcdAgentLogged(): any {
        return this.isCcdAgent() && this.contactService.userContact.ccdAgent.isCCDAgentLogged;
    }
}
